import React, { forwardRef } from 'react';
import PropTypes from 'prop-types';
import { MButton } from '../../atoms/MButton';
import { MSelect } from '../../atoms/MSelect';
import { MText } from '../../atoms/MText';
import { MBox } from '../../atoms/MBox';

import { StyledProductActionButtonsCon } from './skins';

const getActionButtonWidth = (
  widthProp,
  widthStyleProp,
  isVerticalLayout,
  isButtonHidden,
) => {
  const parsedWidth = parseInt(widthProp, 10);
  const isButtonWidthNotSet = !isButtonHidden
    ? Number.isNaN(parsedWidth) || parsedWidth === 0
    : true;
  const isAddFullWidthClass = true;

  if (isButtonWidthNotSet) {
    if (isVerticalLayout) {
      return ['auto', !isAddFullWidthClass, isButtonWidthNotSet];
    }
    if (widthStyleProp === 'fitContent') {
      return ['calc(50% - 4px)', !isAddFullWidthClass, isButtonWidthNotSet];
    }
    if (widthStyleProp === 'fullWidth') {
      return ['100%', isAddFullWidthClass, isButtonWidthNotSet];
    }
  }

  return [widthProp, !isAddFullWidthClass, isButtonWidthNotSet];
};

const VariantFiller = ({
  variantFiller = '',
  variantFillerStyles = {},
  className = 'mr-product-item__action-filler',
}) => (
  <MBox
    className={className}
    border={variantFillerStyles.type === 'button' ? '1px solid' : ''}
    width={variantFillerStyles.width}
  >
    <MText {...variantFillerStyles}>{variantFiller}</MText>
  </MBox>
);

VariantFiller.propTypes = {
  variantFiller: PropTypes.string,
  variantFillerStyles: PropTypes.object,
  className: PropTypes.string,
};

const ProductActionButtons = forwardRef((props, ref) => {
  const {
    layout,
    addVerticalFullWidth = false,
    isVerticalCenterAligned = false,
    showVariantSelector,
    isActionsOrderReversed = false,
    showSkipButton,
    showATCButton,
    variantFiller,
    variantFillerStyles,
    handleVariantChange,
    onAddClick,
    onSkipClick,
    withoutFirstAction = false,
    withoutSecondAction = false,
    variantAtcStatus,
    isMaxQtyAdded,
    isSkipATCIntermediateState,
  } = props;

  const isVerticalLayout = layout === 'vertical';

  const [
    atcButtonWidth,
    isAddFullWidthClassByATC,
    isATCButtonWidthNotSet,
  ] = getActionButtonWidth(
    props.atcButtonWidth,
    props.atcButtonWidthStyle,
    isVerticalLayout,
    withoutSecondAction,
  );
  const [
    variantSelectorWidth,
    isAddFullWidthClassByVS,
    isVariantSelectorWidthNotSet,
  ] = getActionButtonWidth(
    props.variantSelectorWidth,
    props.variantSelectorWidthStyle,
    isVerticalLayout,
    withoutFirstAction,
  );

  const [
    skipButtonWidth,
    isAddFullWidthClassBySkip,
    isSkipButtonWidthNotSet,
  ] = getActionButtonWidth(
    props.skipButtonWidth,
    props.skipButtonWidthStyle,
    isVerticalLayout,
    !showSkipButton,
  );

  const isVerticalFullWidth =
    isVerticalLayout &&
    (addVerticalFullWidth ||
      (!isATCButtonWidthNotSet ||
        !isVariantSelectorWidthNotSet ||
        (showSkipButton && !isSkipButtonWidthNotSet)));

  const isHorizontalFullWidth =
    isAddFullWidthClassByATC ||
    isAddFullWidthClassByVS ||
    (showSkipButton && !isAddFullWidthClassBySkip);

  let flexDirection = isVerticalLayout ? 'column' : 'row';
  if (isActionsOrderReversed) {
    flexDirection += '-reverse';
  }

  const isATCEnabled = showATCButton && !withoutSecondAction;
  let atcStatusText = props.atcButtonText;

  if (isATCEnabled && variantAtcStatus.adding)
    atcStatusText = props.atcProductAddingText;
  else if (
    isATCEnabled &&
    !isSkipATCIntermediateState &&
    (variantAtcStatus.added || isMaxQtyAdded)
  )
    atcStatusText = props.atcProductAddedText;

  let isATCInIntermediateState =
    isATCEnabled &&
    (variantAtcStatus.adding || variantAtcStatus.added || isMaxQtyAdded);

  if (isSkipATCIntermediateState) {
    isATCInIntermediateState = isATCEnabled && variantAtcStatus.adding;
  }

  return (
    <StyledProductActionButtonsCon
      ref={ref}
      className={`mr-product-item__actions ${
        isVerticalLayout
          ? 'mr-product-item__actions--vertical'
          : 'mr-product-item__actions--horizontal'
      } ${
        isVerticalFullWidth
          ? `mr-product-item__actions--vertical-full-width ${
              isVerticalCenterAligned
                ? 'mr-product-item__actions--vertical-center'
                : ''
            }`
          : ''
      } ${
        isHorizontalFullWidth
          ? 'mr-product-item__actions--horizontal-full-width'
          : ''
      }`}
      gap="8px"
      flexDir={flexDirection}
      flexWrap={showSkipButton ? 'wrap-reverse' : 'wrap'}
    >
      <>
        {showSkipButton ? (
          <MButton
            onClick={onSkipClick}
            className={`mr-actions__skip-button ${
              !isSkipButtonWidthNotSet ? 'mr-actions--absolute-width' : ''
            }`}
            buttonStyle={props.skipButtonStyle}
            backgroundColor={props.skipButtonBackgroundColor}
            borderColor={props.skipButtonBorderColor}
            borderRadius={props.skipButtonBorderRadius}
            textColor={props.skipButtonTextColor}
            fontSize={props.skipButtonFontSize}
            mobileFontSize={props.skipButtonMobileFontSize}
            fontStyle={props.skipButtonFontStyle}
            fontWeight={props.skipButtonFontWeight}
            textDecoration={props.skipButtonTextDecoration}
            textAlignment={props.skipButtonTextAlignment}
            fontFamily={props.skipButtonFontFamily}
            padding={props.skipButtonPadding}
            margin={props.skipButtonMargin}
            height={props.skipButtonHeight}
            width={skipButtonWidth}
          >
            {props.skipButtonText}
          </MButton>
        ) : (
          <MBox
            width={variantSelectorWidth}
            className={`mr-actions__action-filler-con ${
              !showVariantSelector
                ? 'mr-actions__action-filler-con--hidden'
                : ''
            } ${
              !isVariantSelectorWidthNotSet ? 'mr-actions--absolute-width' : ''
            }`}
            margin={props.variantSelectorMargin}
          >
            {!isVerticalLayout && (
              <VariantFiller
                variantFiller={variantFiller}
                variantFillerStyles={variantFillerStyles}
              />
            )}
            <MSelect
              className={`mr-actions__variant-selector ${
                !showVariantSelector
                  ? 'mr-actions__variant-selector--hidden'
                  : ''
              }`}
              onChange={handleVariantChange}
              value={props.variantSelectorValue}
              options={props.variantSelectorOptions}
              style={props.variantSelectorStyle}
              backgroundColor={props.variantSelectorBackgroundColor}
              borderColor={props.variantSelectorBorderColor}
              borderRadius={props.variantSelectorBorderRadius}
              color={props.variantSelectorTextColor}
              fontSize={props.variantSelectorFontSize}
              mobileFontSize={props.variantSelectorMobileFontSize}
              fontStyle={props.variantSelectorFontStyle}
              fontWeight={props.variantSelectorFontWeight}
              textDecoration={props.variantSelectorTextDecoration}
              fontFamily={props.variantSelectorFontFamily}
              textAlignment={props.variantSelectorTextAlignment}
              height={props.variantSelectorHeight}
              padding={props.variantSelectorPadding}
            />
          </MBox>
        )}
      </>
      {showATCButton &&
        !withoutSecondAction && (
          <MButton
            onClick={onAddClick}
            className={`mr-actions__atc-button ${
              !isATCButtonWidthNotSet ? 'mr-actions--absolute-width' : ''
            }`}
            buttonStyle={props.atcButtonStyle}
            backgroundColor={props.atcButtonBackgroundColor}
            borderColor={props.atcButtonBorderColor}
            borderRadius={props.atcButtonBorderRadius}
            textColor={props.atcButtonTextColor}
            fontSize={props.atcButtonFontSize}
            mobileFontSize={props.atcButtonMobileFontSize}
            fontStyle={props.atcButtonFontStyle}
            fontWeight={props.atcButtonFontWeight}
            textDecoration={props.atcButtonTextDecoration}
            textAlignment={props.atcButtonTextAlignment}
            fontFamily={props.atcButtonFontFamily}
            padding={props.atcButtonPadding}
            margin={props.atcButtonMargin}
            height={props.atcButtonHeight}
            width={atcButtonWidth}
            disabled={isATCInIntermediateState}
            opacity={isATCInIntermediateState ? '0.7' : '1'}
          >
            {atcStatusText}
          </MButton>
        )}
      {isVerticalLayout &&
        !isVerticalFullWidth && (
          <VariantFiller
            variantFiller={variantFiller}
            variantFillerStyles={variantFillerStyles}
            className="mr-product-item__action-filler mr-product-item__action-filler--vertical"
          />
        )}
    </StyledProductActionButtonsCon>
  );
});

ProductActionButtons.propTypes = {
  layout: PropTypes.string,
  addVerticalFullWidth: PropTypes.bool,
  isVerticalCenterAligned: PropTypes.bool,
  variantFiller: PropTypes.string,
  variantFillerStyles: PropTypes.oneOfType([
    PropTypes.object,
    PropTypes.string,
  ]),
  showVariantSelector: PropTypes.bool.isRequired,
  showATCButton: PropTypes.bool.isRequired,
  isActionsOrderReversed: PropTypes.bool,
  handleVariantChange: PropTypes.func.isRequired,
  onAddClick: PropTypes.func.isRequired,
  atcButtonText: PropTypes.string.isRequired,
  atcButtonTextColor: PropTypes.string.isRequired,
  atcButtonStyle: PropTypes.string.isRequired,
  atcButtonBackgroundColor: PropTypes.string.isRequired,
  atcButtonBorderColor: PropTypes.string.isRequired,
  atcButtonBorderRadius: PropTypes.string.isRequired,
  atcButtonFontSize: PropTypes.string.isRequired,
  atcButtonMobileFontSize: PropTypes.string.isRequired,
  atcButtonFontStyle: PropTypes.string.isRequired,
  atcButtonFontWeight: PropTypes.string.isRequired,
  atcButtonTextDecoration: PropTypes.string.isRequired,
  atcButtonTextAlignment: PropTypes.string.isRequired,
  atcButtonFontFamily: PropTypes.string.isRequired,
  atcButtonPadding: PropTypes.string.isRequired,
  atcButtonMargin: PropTypes.string.isRequired,
  atcButtonHeight: PropTypes.string.isRequired,
  atcButtonWidth: PropTypes.string.isRequired,
  atcButtonWidthStyle: PropTypes.string.isRequired,
  variantSelectorValue: PropTypes.string.isRequired,
  variantSelectorOptions: PropTypes.array.isRequired,
  variantSelectorTextColor: PropTypes.string.isRequired,
  variantSelectorStyle: PropTypes.string.isRequired,
  variantSelectorBackgroundColor: PropTypes.string.isRequired,
  variantSelectorBorderColor: PropTypes.string.isRequired,
  variantSelectorBorderRadius: PropTypes.string.isRequired,
  variantSelectorFontSize: PropTypes.string.isRequired,
  variantSelectorMobileFontSize: PropTypes.string.isRequired,
  variantSelectorFontStyle: PropTypes.string.isRequired,
  variantSelectorFontWeight: PropTypes.string.isRequired,
  variantSelectorTextDecoration: PropTypes.string.isRequired,
  variantSelectorTextAlignment: PropTypes.string.isRequired,
  variantSelectorFontFamily: PropTypes.string.isRequired,
  variantSelectorPadding: PropTypes.string.isRequired,
  variantSelectorMargin: PropTypes.string.isRequired,
  variantSelectorHeight: PropTypes.string.isRequired,
  variantSelectorWidth: PropTypes.string.isRequired,
  variantSelectorWidthStyle: PropTypes.string.isRequired,
  withoutFirstAction: PropTypes.bool,
  withoutSecondAction: PropTypes.bool,
  showSkipButton: PropTypes.bool,
  onSkipClick: PropTypes.func,
  variantAtcStatus: PropTypes.object,
  skipButtonWidth: PropTypes.string,
  skipButtonWidthStyle: PropTypes.string,
  skipButtonStyle: PropTypes.string,
  skipButtonBackgroundColor: PropTypes.string,
  skipButtonBorderColor: PropTypes.string,
  skipButtonBorderRadius: PropTypes.string,
  skipButtonTextColor: PropTypes.string,
  skipButtonFontSize: PropTypes.string,
  skipButtonMobileFontSize: PropTypes.string,
  skipButtonFontStyle: PropTypes.string,
  skipButtonFontWeight: PropTypes.string,
  skipButtonTextDecoration: PropTypes.string,
  skipButtonTextAlignment: PropTypes.string,
  skipButtonFontFamily: PropTypes.string,
  skipButtonPadding: PropTypes.string,
  skipButtonMargin: PropTypes.string,
  skipButtonHeight: PropTypes.string,
  skipButtonText: PropTypes.string,
  isMaxQtyAdded: PropTypes.bool,
  atcProductAddingText: PropTypes.string,
  atcProductAddedText: PropTypes.string,
  isSkipATCIntermediateState: PropTypes.bool,
};

export default ProductActionButtons;
